import { LinkIcon } from '@phosphor-icons/react';
import {
  Avatar,
  Badge,
  Button,
  Cell,
  Column,
  Modal,
  Row,
  SearchField,
  type Selection,
  Spinner,
  Table,
  TableBody,
  TableContainer,
  TableHeader,
} from '@skupreme/shared/ui';
import { useDebouncedMemo } from '@hooks/debounceAsync';
import { useQuery } from '@tanstack/react-query';
import { CommonUtility } from '@utility/common';
import CatalogService from '@utility/services/catalog';
import { useEffect, useState } from 'react';
import { type AliasMatchItem, isUnmatchable } from '../../lib/missing-info';

const noItems: AliasMatchItem[] = [];

/*
 * Catalog items in the region an alias can be matched to. The search starts
 * as the alias itself, which also finds the items that already hold it.
 */
function useAliasMatchOptions(regionId: string, search: string) {
  const applied = useDebouncedMemo<string>(() => search.trim(), [search], 300);

  const query = useQuery({
    queryKey: ['storage-providers', 'alias-match', regionId, applied ?? ''],
    queryFn: async ({ signal }) =>
      (
        await CatalogService.getAutocomplete(
          { search: applied, marketplace_region_id: regionId },
          signal,
        )
      )?.data as AliasMatchItem[],
    enabled: !!regionId && !!applied,
  });

  return {
    items: (applied && query.data) || noItems,
    loading: query.isFetching,
  };
}

export type MatchAliasModalProps = {
  /** The 3PL alias being matched; set shows the dialog. */
  alias: string | null;
  regionId: string;
  onClose: () => void;
  /** Called with the catalog item the alias goes to. */
  onMatch: (itemId: string) => void;
  isProcessing?: boolean;
};

/** Matches an alias the 3PL reports to an item of the region's catalog. */
export function MatchAliasModal({
  alias,
  regionId,
  onClose,
  onMatch,
  isProcessing,
}: MatchAliasModalProps) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const { items, loading } = useAliasMatchOptions(
    alias ? regionId : '',
    search,
  );

  useEffect(() => {
    if (alias) {
      setSearch(alias);
      setSelected(null);
    }
  }, [alias]);

  return (
    <Modal
      isOpen={!!alias}
      onOpenChange={isOpen => !isOpen && onClose()}
      size="md"
      showCloseButton
      isDismissable={!isProcessing}
      title={
        <span className="sk:flex sk:flex-col sk:gap-0.5">
          Match to catalog item
          <span className="sk:truncate sk:text-dense sk:font-normal sk:text-fg-secondary">
            3PL alias {alias}
          </span>
        </span>
      }
      footer={
        <div className="sk:flex sk:justify-end sk:gap-2">
          <Button
            variant="pill"
            size="md"
            isDisabled={isProcessing}
            onPress={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            isDisabled={!selected}
            isPending={isProcessing}
            onPress={() => selected && onMatch(selected)}
          >
            <LinkIcon size={14} weight="bold" aria-hidden />
            Match
          </Button>
        </div>
      }
    >
      <div className="sk:flex sk:flex-col sk:gap-3">
        <SearchField
          aria-label="Search catalog"
          placeholder="Search catalog by name, SKU or UPC..."
          value={search}
          onChange={setSearch}
          isLoading={loading}
          autoFocus
          className="sk:w-full"
        />
        <TableContainer className="sk:max-h-96 sk:overflow-y-auto">
          <Table
            aria-label="Catalog items"
            selectionMode="single"
            disallowEmptySelection={false}
            selectedKeys={selected ? [selected] : []}
            disabledKeys={items.filter(isUnmatchable).map(item => item._id)}
            onSelectionChange={(keys: Selection) => {
              const [key] = keys === 'all' ? [] : [...keys];
              setSelected(key ? String(key) : null);
            }}
          >
            <TableHeader>
              <Column id="item" isRowHeader>
                Item
              </Column>
              <Column id="price" className="sk:w-28">
                Price
              </Column>
            </TableHeader>
            <TableBody
              renderEmptyState={() => (
                <div className="sk:flex sk:justify-center sk:px-3 sk:py-8 sk:text-dense sk:text-fg-tertiary">
                  {loading ? (
                    <Spinner size="sm" label="Searching" />
                  ) : search.trim() ? (
                    'No catalog items found'
                  ) : (
                    'Start typing a name or SKU to find the catalog item'
                  )}
                </div>
              )}
            >
              {items.map(item => (
                <Row key={item._id} id={item._id} className="sk:cursor-pointer">
                  <Cell>
                    <div className="sk:flex sk:min-w-0 sk:items-center sk:gap-3">
                      <Avatar
                        src={item.p_images?.display_image}
                        name={item.short_title || item.p_title}
                        shape="square"
                        size="md"
                      />
                      <div className="sk:flex sk:min-w-0 sk:flex-col sk:gap-0.5">
                        <span className="sk:truncate sk:text-sm sk:text-fg">
                          {item.short_title || item.p_title}
                        </span>
                        <span className="sk:flex sk:items-center sk:gap-2 sk:text-dense sk:text-fg-secondary">
                          {item.p_sku}
                          {isUnmatchable(item) ? (
                            <Badge tone="warning">Conflict</Badge>
                          ) : null}
                        </span>
                      </div>
                    </div>
                  </Cell>
                  <Cell className="sk:tabular-nums">
                    {CommonUtility.currencyFormat(
                      item.p_price,
                      item.p_currency,
                    )}
                  </Cell>
                </Row>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </Modal>
  );
}
